<template>
    <grid
        :fetchCallback="fetchCallback"
        :columnDefs="columnDefs"
        :maxBlocksInCache="null"
        :suppressScrollOnNewData="true"
        ref="grid"
        :pinnedBottomRowData="pinnedBottomRowData"
        @on-total-records="setTotalRecords"
        :getRowStyle="rowStyle"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>
        <b-button-group class="d-none d-xl-flex">
            <search-multiselect
                v-model="selectedProperties"
                :options="properties"
                :icon="['fas', 'hotel']"
                class="mr-1"
                variant="primary"
                placeholder="Properties"
                controlValue="Properties"
            ></search-multiselect>
        </b-button-group>
        <b-button-group class="d-none d-xl-flex">
            <search-multiselect
                v-model="selectedFilters"
                :options="filterByBlockTypeOptions"
                class="mr-1"
                variant="primary"
                controlValue="Block Type"
            ></search-multiselect>
        </b-button-group>
        <responsive-button :href="excelHref" variant="primary" :icon="['fas', 'file-excel']"></responsive-button>
        <responsive-button variant="primary" @click="refresh" :icon="['fas', 'arrows-rotate']"
            >Refresh</responsive-button
        >
        <responsive-button variant="primary" @click="resetFilters" :icon="['fas', 'arrow-rotate-right']"
            >Reset</responsive-button
        >
        <responsive-button
            variant="primary"
            href="/app/calendar-tape/view/"
            :icon="['fas', 'plus']"
        ></responsive-button>
        <b-button-group class="d-xl-none">
            <more-filters-dropdown>
                <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                    <b-button-group>
                        <search-multiselect
                            v-model="selectedProperties"
                            :options="properties"
                            :icon="['fas', 'hotel']"
                            class="mr-1"
                            variant="primary"
                            placeholder="Properties"
                            controlValue="Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        ></search-multiselect>
                    </b-button-group>
                    <b-button-group>
                        <search-multiselect
                            v-model="selectedFilters"
                            :options="filterByBlockTypeOptions"
                            class="mr-1"
                            variant="primary"
                            placeholder="Block Type"
                            controlValue="Flags"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        ></search-multiselect>
                    </b-button-group>
                </template>
            </more-filters-dropdown>
        </b-button-group>
    </grid>
</template>

<script>
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import API from '../../../api';
    import grid from '../../generic/grid/grid.vue';
    import { escapeHTML } from '../../../utils/strings';
    import DateRangePicker, {
        ALL_DATES,
        LAST_MONTH,
        LAST_YEAR,
        LAST_WEEK,
        THIS_WEEK,
        nameToRange,
        THIS_MONTH,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
        FUTURE,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import format from 'date-fns/format';
    import { mapGetters } from 'vuex';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import {
        RESERVATION_BLOCKED_GRID_ENDPOINT,
        RESERVATION_BLOCKED_DELETE_ENDPOINT,
        RESERVATION_BLOCKED_EDIT_ENDPOINT,
        REPORT_PROPERTIES_ALL,
    } from '../../../endpoints';
    import responsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';

    export default {
        components: {
            grid,
            responsiveButton,
            DateRangePicker,
            SearchMultiselect,
            ResponsiveDropdown,
            MoreFiltersDropdown,
            actionsCellRenderer: ActionsCellRenderer,
        },
        data() {
            return {
                dateRange: nameToRange(FUTURE),
                dateRequestFormat: 'yyyy-MM-dd',
                desc: false,
                limit: 20,
                offset: 0,
                predefinedRanges: [
                    FUTURE,
                    TODAY,
                    YESTERDAY,
                    THIS_WEEK,
                    LAST_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                ],
                properties: [],
                sort: 'created',
                search: '',
                selectedProperties: [],
                selectedFilters: [],
                totalRecords: 0,
                rowStyle: null,
            };
        },
        beforeMount() {
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('reservationsStore', ['guestEdit', 'reservationEdit', 'reservationView']),
            pinnedBottomRowData() {
                return [
                    {
                        number: this.totalRecords,
                        id: this.totalRecords,
                    },
                ];
            },
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        field: 'number',
                        minWidth: 30,
                        maxWidth: 80,
                        sortable: false,
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                    },
                    {
                        field: 'id',
                        headerName: 'ID',
                        maxWidth: 100,
                        sort: 'desc',
                        valueGetter: (params) => {
                            return params.node.rowPinned ? '' : params.node.id;
                        },
                    },
                    {
                        field: 'created',
                        headerName: 'Created',
                        type: 'dateColumn',
                        minWidth: 105,
                        maxWidth: 130,
                    },
                    {
                        field: 'from_date',
                        headerName: 'From',
                        type: 'dateColumn',
                        minWidth: 105,
                        maxWidth: 130,
                    },

                    {
                        field: 'to_date',
                        headerName: 'To',
                        type: 'dateColumn',
                        minWidth: 105,
                        maxWidth: 130,
                    },
                    {
                        field: 'properties',
                        headerName: 'Property',
                        cellRenderer: ({ data, node }) => {
                            if (!node.rowPinned) {
                                return data.properties
                                    .map((item) => {
                                        return escapeHTML(item.name);
                                    })
                                    .join('<br />');
                            }
                        },
                        autoHeight: true,
                    },
                    {
                        field: 'entered_by_first_name',
                        headerName: 'First Name',
                    },
                    {
                        field: 'entered_by_last_name',
                        headerName: 'Last Name',
                    },
                    {
                        field: 'block_type',
                        headerName: 'Block Type',
                        minWidth: 100,
                        maxWidth: 130,
                        sortable: false,
                    },
                    {
                        field: 'notes',
                        headerName: 'Notes',
                        autoHeight: true,
                        cellStyle: { whiteSpace: 'normal', lineHeight: '35px' },
                        autoHeight: true,
                    },
                    {
                        field: '',
                        headerName: '',
                        cellRenderer: 'actionsCellRenderer',
                        sortable: false,
                        minWidth: 130,
                        maxWidth: 130,
                        cellRendererParams: {
                            deleteReservation: async (id) => {
                                try {
                                    await API.delete(RESERVATION_BLOCKED_DELETE_ENDPOINT(id));
                                    this.successToast({
                                        text: 'Reservation has been deleted',
                                    });
                                    this.$bvModal.hide('deleteModalId' + id);
                                    this.refresh();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not delete reservation',
                                    });
                                }
                            },
                            editReservation: async (id, notes) => {
                                try {
                                    await API.patch(RESERVATION_BLOCKED_EDIT_ENDPOINT(id), { notes });
                                    this.$bvModal.hide('editModalId' + id);
                                    this.refresh();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not update reservation',
                                    });
                                }
                            },
                        },
                    },
                ];
            },
            excelHref() {
                return `/app/reservations-list/blocked-reservations/excel/?offset=${this.offset}&limit=${
                    this.limit
                }&sort=${this.sort}&desc=${this.desc}&start_date=${this.startDateRequest}&end_date=${
                    this.endDateRequest
                }&${this.selectedProperties && 'properties=' + this.selectedProperties.join(',')}&${
                    this.selectedFilters && 'block_type=' + this.selectedFilters.join(',')
                }`;
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get(RESERVATION_BLOCKED_GRID_ENDPOINT(), {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            properties: this.selectedProperties.join(','),
                            block_type: this.selectedFilters.join(','),
                        },
                    });
                };
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            filterByBlockTypeOptions() {
                return [
                    { text: 'Subscriber', value: 'SUBSCRIBER' },
                    { text: 'Team Member', value: 'TEAM_MEMBER' },
                    { text: 'Owner', value: 'OWNER' },
                    { text: 'Other', value: 'OTHER' },
                ];
            },
        },
        mounted() {
            this.fetchProperties();
        },
        methods: {
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            async fetchProperties() {
                try {
                    const { data } = await API.get(REPORT_PROPERTIES_ALL());
                    this.properties = data.map((item) => ({
                        value: item.id,
                        text: item.name,
                        disabled: item.disabled,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not load properties' });
                }
            },
            resetFilters() {
                this.selectedProperties = [];
                this.selectedFilters = [];
            },
            setTotalRecords(value) {
                this.totalRecords = value;
            },
        },
        watch: {
            selectedProperties() {
                this.refresh();
            },
            selectedFilters() {
                this.refresh();
            },
        },
    };
</script>
